import React, { useEffect, useState, useMemo } from 'react';
import { toastr } from 'react-redux-toastr';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from '@/hooks';
import { makeStyles } from '@mui/styles';
import { isEqual } from 'underscore';
import { isSameDay } from 'date-fns';
import { RangeKeyDict } from 'react-date-range';
import { push } from 'redux-first-history';
import type { Params, NavigateFunction } from 'react-router-dom';

import icon0 from '../Common/Icons/svg/half-map-1-01.svg';
import icon2 from '../Common/Icons/svg/half-map-1-03.svg';
import icon0Highlight from '../Common/Icons/svg/half-map-orange-01.svg';
import icon2Highlight from '../Common/Icons/svg/half-map-orange-03.svg';
import { i18next } from '@/config/i18n';
import { dispatch_theme } from '@/config/colors';
import { urls } from '@/config/urls';
import {
  getRoutesByProcess,
  lazyRouteUpdate,
} from '@/actions/routes/routesThunks';
import {
  setDayData,
  setDeletedRoutes,
  setProcess,
  setTimelineHighlights,
  setTimelineRange,
  setIsMustModeEnabled,
  setIsUpdatePlanningRunning,
  setLockedVehicles,
  resetReassignedShipments,
} from '@/actions/routes/routesPure';
import useDebounce from '../Common/hooks/debounceHook';
import Picker from './Picker';
import { CNAME, ROUTELIST, ROUTES } from '@/config/paths';
import { AppDispatch, RootState } from '../../index';
import { SetOptimizationData } from '@/actions/common/commonPure';

import {
  Route,
  TimelineHighlight,
  TLRange,
  ToolbarState,
} from '@/config/types/Routes';
import { customername } from '@/index';


const mapIcons = {
  one: {
    regular: <img alt={'route-nav-0'} src={icon0} width="40" height="40" />,
    highlight: (
      <img alt={'route-nav-0'} src={icon0Highlight} width="40" height="40" />
    ),
  },
  two: {
    regular: <img alt={'route-nav-2'} src={icon2} width="40" height="40" />,
    highlight: (
      <img alt={'route-nav-2'} src={icon2Highlight} width="40" height="40" />
    ),
  },
};

const useStyles = makeStyles((theme: typeof dispatch_theme) => ({
  spacing: {
    padding: theme.spacing(1),
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  ellipseText: {
    maxWidth: 100,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  resetProcess: {
    marginLeft: theme.spacing(2),
  },
  leftToolbar: {
    paddingLeft: theme.spacing(2),
  },
  deleteIcon: {
    color: theme.palette.grey[900],
  },
  exportIcon: {
    alignSelf: 'center',
    marginLeft: '35px',
    marginRight: '10px'
  },
  confirmUpdateIcon: {
    color: theme.palette.success.main,
    height: '50px',
    width: '50px'
  },
  cancelUpdateIcon: {
    color: theme.palette.error.main,
    height: '50px',
    width: '50px'
  },
  buttonsContainer: {
    width: '100%',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between'
  },
  updatePlanningContainer: {
    height: '56px',
    maxWidth: '140px',
    border: '1px solid rgba(0, 0, 0, 0.3)',
    borderRadius: '4px',
    backgroundColor: 'rgba(239, 89, 39, 0.3)',
  },
  mustModeContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: '10px'
  },
  shipmentsIcon: {
    alignSelf: 'start'
  },
  shipmentsNumber: {
    textAlign: 'center'
  },
  shipmentsNumContainer: {
    marginTop: '0px'
  },
  shipmentsNumInnerContainer: {
    border: '1px solid rgba(0, 0, 0, 0.3)',
    borderRadius: '4px',
    width: '176px',
    display: 'flex',
    padding: '5px',
  }
}));

export type TLToolabrProps = {
  router: {
    location: any;
    navigate: NavigateFunction;
    params: Params;
  };
}

const TLToolbar: React.FC<TLToolabrProps> = ({ router }) => {
  const [activeView, setActiveView] = useState<number>(0);
  const [areThereDeletedRoutesInPlanning, setAreThereDeletedRoutesInPlanning] = useState<boolean>(false);
  const [pickDates, setPicker] = useState<boolean>(false);
  const {
    routes,
    deletedRoutes,
    timelineRange: range,
    highlights,
    isMustModeEnabled,
  } = useSelector((state: RootState) => state.routes, isEqual);
  const selected_process = useSelector(
    (state: RootState) => state.core.selected_process
  );
  const loading = useSelector(
    (state: RootState) => state.core.loading
  );
  const [selectionRange, setRange] = useState<TLRange>({ ...range });
  const {
    location: { pathname },
  } = router;
  const classes = useStyles();
  const dispatch: AppDispatch = useDispatch();
  const onSetTimelineHighlights = (data: TimelineHighlight[]) =>
    dispatch(setTimelineHighlights(data));
  const onLocationChange = (path: string) => dispatch(push(path));
  const onSetPagination = (page: number) => dispatch(lazyRouteUpdate(page));
  const onSetTimelineRange = (newRange: TLRange) =>
    dispatch(setTimelineRange(newRange));
  const onSetProcess = (uuid: string) => dispatch(setProcess(uuid));
  const activeRoute = (path: string) => router.location.pathname.startsWith(path);

  useEffect(() => {

    return () => {
      onSetTimelineHighlights([]);
      onSetProcess(undefined);
      dispatch(
        setDayData({
          colors: {},
          available: false,
          routes: {},
          routestrings: {},
        })
      );
    };
    // TODO: check what deps are really needed
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const view = useMemo(() => {
    if (pathname.includes('/routes/timeline/')) return 'Timeline';
    if (pathname.includes('/routes/routelistmap/')) return 'Map';
    return 'Map';
  }, [pathname]);

  /**
   * @description - this hook will listen for changes in the selected process and fetch according route data / routestrings
   */
  useEffect(() => {
    if (selected_process) {
      resetFilter();

      dispatch(getRoutesByProcess(selected_process, true, view));
    }
    // TODO: check what deps are really needed
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected_process?.optimization_id, view]);


  useEffect(() => {
    if (deletedRoutes?.[selected_process?.optimization_id]?.length) {
      setAreThereDeletedRoutesInPlanning(true);
    } else {
      setAreThereDeletedRoutesInPlanning(false);
    }
  }, [deletedRoutes, selected_process?.optimization_id]);

  useEffect(() => {
    if (pathname.includes('/routes/timeline/')) {
      setActiveView(0);
    } else if (pathname.includes('/routes/routelistmap/')) {
      setActiveView(1);
    }
  }, [pathname]);

  function applyTimeWindow() {
    setPicker(false);
    resetFilter();
    onSetProcess(undefined);
    onSetPagination(undefined);
    onSetTimelineRange(selectionRange);
    if (urls.intern.routes.detail(pathname)) {
      onLocationChange(`${CNAME}${ROUTES}/${ROUTELIST}`);
    }
  }

  function searchInRoute(
    { deliveries, custom_id }: Route,
    query: string
  ) {
    let driverMatch;
    const matches = deliveries.filter((delivery) => {
      const {
        address: { city, street,}, custom_id, drivernotes, notes } = delivery;
    });
    const routeMatch =
      custom_id.toLowerCase().indexOf(query.toLowerCase()) !== -1;

    return {
      matches: matches,
      routeMatch: routeMatch || driverMatch,
    };
  }

  function handleSearch(value: string) {
    const routesToFilter: TimelineHighlight[] = [];
    const routesArray: Route[] = Object.values(routes);
    for (const route of routesArray) {
      const { matches, routeMatch } = searchInRoute(route, value);
      if (matches.length) {
        routesToFilter.push({ id: route.id, deliveries: matches });
      }
      // IF NO DELIVERIES MATCHED THE QUERY, WE CHECK IF THERE WAS A MATCH ON THE ROUTE OBJECT
      if (!matches.length && routeMatch) {
        routesToFilter.push({ id: route.id, deliveries: [] });
      }
    }
    return filterRoutes(routesToFilter);
  }

  function filterRoutes(query: TimelineHighlight[]) {
    const filteredRoutes = [];
    if (!query.length) {
      return resetFilter(false);
    }
    query.map((item: TimelineHighlight) => {
      const route = routes[item.id];
      if (route) {
        filteredRoutes.push(route);
      }
    });
    onSetTimelineHighlights(query);
  }

  function resetFilter(resetValue: boolean = true) {
    onSetTimelineHighlights([]);
    if (resetValue) {
      //setState({ ...state, searchVal: '' });
    }
  }

  function renderSearchCount(highlights: TimelineHighlight[]) {
    return highlights.reduce((acc: number, val: TimelineHighlight) => {
      return val.deliveries ? acc + val.deliveries.length : acc;
    }, 0);
  }

  function renderResult() {
    if (!highlights.length) {
      return `${i18next.t('common:routes.noMatches')}`;
    }
    const deliveryMatches = renderSearchCount(highlights);
    const resultString = `${i18next.t('common:routes.matching')}
    ${deliveryMatches
        ? deliveryMatches + i18next.t('common:routes.deliveriesIn')
        : ''
      }
    ${highlights.length} ${i18next.t('common:routes.tours')}`;
    return resultString;
  }


  function handleRangeSelect(ranges: RangeKeyDict) {
    setRange({
      startDate: ranges.selection.startDate,
      endDate: ranges.selection.endDate,
      key: 'selection',
      isSame: isSameDay(ranges.selection.startDate, ranges.selection.endDate),
      color: dispatch_theme.palette.primary.main,
    });
  }

  let _icon0 = mapIcons.one.regular;
  let _icon1 = mapIcons.two.regular;
  switch (pathname) {
    case urls.intern.routes.main:
      _icon0 = mapIcons.one.highlight;
      break;
    case urls.intern.routes.routesmap:
      _icon1 = mapIcons.two.highlight;
      break;
    //@ts-ignore
    case urls.intern.routes.detail(location):
    default:
      _icon1 = mapIcons.two.highlight;
  }

  const cancelUpdatePlanning = () => {
    const deletedRoutesCopy = { ...deletedRoutes };
    delete deletedRoutesCopy[selected_process?.optimization_id];
    dispatch(setDeletedRoutes({ ...deletedRoutesCopy }));
    dispatch(setLockedVehicles([]));
    dispatch(resetReassignedShipments());
  };

  const confirmUpdatePlanning = () => {
    dispatch(setIsUpdatePlanningRunning(true));
    setTimeout(() => {
      toastr.success('', i18next.t('common:routes.planningWasUpdated'));
      dispatch(setIsUpdatePlanningRunning(false));
      cancelUpdatePlanning();
    }, 3000);
  };

  const handleToggleMustMode = () => {
    dispatch(setIsMustModeEnabled(!isMustModeEnabled));
  };

  return (
    <Card id="routes-toolbar">
      <Dialog
        open={pickDates}
        onClose={() => setPicker(!pickDates)}
        maxWidth={'md'}
        data-cy={'rangepicker'}
      >
        <DialogTitle><>{i18next.t('common:routes.filterByDate')}</></DialogTitle>
        <DialogContent dividers>
          <Picker ranges={[selectionRange]} onChange={handleRangeSelect} />
        </DialogContent>
        <DialogActions>
          <Button
            color={'primary'}
            key={'btn_apply_date'}
            variant={'contained'}
            data-cy={'apply-date'}
            onClick={applyTimeWindow}
          >
            <>{i18next.t('common:buttons.apply')}</>
          </Button>
          <Button
            color={'inherit'}
            style={{ marginLeft: 16 }}
            key={'btn_cancel_datepick'}
            variant={'contained'}
            onClick={() => setPicker(!pickDates)}
          >
            <>{i18next.t('common:buttons.cancel')}</>
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default withRouter(TLToolbar);
